/**
 * Resolve a logo URL for dashboard / chain lists.
 * Order: API (absolute or site-relative) → Trust Wallet → DefiLlama icons CDN → null (use monogram).
 */

const API_BASE = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8002"
).replace(/\/$/, "");

function isHttpUrl(s) {
  return typeof s === "string" && /^https?:\/\//i.test(s.trim());
}

/** Turn API-relative paths into absolute URLs. */
function normalizeAssetUrl(u) {
  if (u == null || typeof u !== "string") return null;
  const t = u.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("//")) return `https:${t}`;
  if (t.startsWith("/")) return `${API_BASE}${t}`;
  return null;
}

function trustWalletLogo(blockchainId) {
  if (!blockchainId || typeof blockchainId !== "string") return null;
  const id = blockchainId.trim().toLowerCase();
  if (!/^[a-z0-9._-]+$/.test(id)) return null;
  return `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/${id}/info/logo.png`;
}

function llamaChainLogo(slug) {
  if (!slug || typeof slug !== "string") return null;
  const s = slug.trim().toLowerCase();
  if (!/^[a-z0-9._-]+$/.test(s)) return null;
  return `https://icons.llamao.fi/icons/chains/rsz_${s}.jpg`;
}

function lavaNetworkLogo() {
  return "https://logo.svgcdn.com/token-branded/lava-network.png";
}

function normalizeChainSlug(rawSlug) {
  if (typeof rawSlug !== "string" || !rawSlug.trim()) return null;
  let slug = rawSlug.trim().toLowerCase();
  slug = slug.replace(/^(.*?)-(mainnet|testnet|devnet|previewnet|stagenet)$/i, "$1");
  slug = slug.replace(/-(grpc|rest|websocket|ws)$/i, "");
  slug = slug.replace(/-(ethereum|polygon|solana|osmosis|axelar|akash|lava)$/i, "");
  return slug;
}

function logoFromApi(chain) {
  if (!chain || typeof chain !== "object") return null;
  const keys = [
    "logo_url",
    "logo",
    "icon_url",
    "icon",
    "image_url",
    "chain_logo",
    "logo_uri",
  ];
  for (const k of keys) {
    const raw = chain[k];
    if (typeof raw !== "string") continue;
    const abs = isHttpUrl(raw) ? raw.trim() : normalizeAssetUrl(raw);
    if (abs) return abs;
  }
  return null;
}

function pickNumericChainId(chain) {
  const v =
    chain.chain_id ??
    chain.chainId ??
    chain.evm_chain_id ??
    chain.evmChainId ??
    chain.network_id;
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Common EVM / L2 chain IDs → Trust Wallet `blockchains/<id>` folder name */
const CHAIN_ID_TO_TRUSTWALLET = {
  1: "ethereum",
  10: "optimism",
  25: "cronos",
  56: "smartchain",
  100: "xdai",
  137: "polygon",
  169: "manta",
  196: "xlayer",
  204: "opbnb",
  250: "fantom",
  288: "boba",
  324: "zksync",
  1101: "polygonzkevm",
  1284: "moonbeam",
  1285: "moonriver",
  1329: "sei",
  1337: "immutable",
  1442: "polygonzkevm",
  1868: "soneium",
  2222: "kava",
  2741: "abstract",
  5000: "mantle",
  5330: "superseed",
  59144: "linea",
  81457: "blast",
  8453: "base",
  34443: "mode",
  42161: "arbitrum",
  42170: "arbitrum",
  42220: "celo",
  43111: "avalanchec",
  43114: "avalanchec",
  48900: "zircuit",
  534352: "scroll",
  7777777: "zora",
  1313161554: "aurora",
};

const SLUG_TO_SOURCE = {
  eth: { tw: "ethereum" },
  ethereum: { tw: "ethereum" },
  bsc: { tw: "smartchain" },
  binance: { tw: "smartchain" },
  matic: { tw: "polygon" },
  polygon: { tw: "polygon" },
  avax: { tw: "avalanchec" },
  avalanche: { tw: "avalanchec" },
  arb: { tw: "arbitrum" },
  arbitrum: { tw: "arbitrum" },
  op: { tw: "optimism" },
  optimism: { tw: "optimism" },
  base: { tw: "base" },
  linea: { tw: "linea" },
  zksync: { tw: "zksync" },
  scroll: { tw: "scroll" },
  blast: { tw: "blast" },
  mantle: { tw: "mantle" },
  metis: { tw: "metis" },
  fantom: { tw: "fantom" },
  ftm: { tw: "fantom" },
  gnosis: { tw: "xdai" },
  xdai: { tw: "xdai" },
  celo: { tw: "celo" },
  cronos: { tw: "cronos" },
  moonbeam: { tw: "moonbeam" },
  aurora: { tw: "aurora" },
  sei: { tw: "sei" },
  osmosis: { tw: "osmosis" },
  axelar: { tw: "axelar" },
  kava: { tw: "kava" },
  filecoin: { tw: "filecoin" },
  noble: { tw: "noble" },
  akash: { tw: "akash" },
  lava: { tw: "lava" },
  cosmos: { tw: "cosmos" },
  solana: { tw: "solana" },
  near: { tw: "near" },
  aptos: { tw: "aptos" },
  sui: { tw: "sui" },
  injective: { tw: "nativeinjective" },
  story: { llama: "story" },
  oasys: { llama: "oasys" },
  fraxtal: { llama: "fraxtal" },
  frax: { llama: "fraxtal" },
  // Explicit URLs for chains not on Trust Wallet or DefiLlama CDNs.
  shannon: { url: "https://pbs.twimg.com/profile_images/1882143278604185600/qfiYA7DL_400x400.jpg" },
  pocket: { url: "https://pbs.twimg.com/profile_images/1882143278604185600/qfiYA7DL_400x400.jpg" },
  canopy: { url: "https://pbs.twimg.com/profile_images/1924482672530763776/nXHpr2Fp_400x400.png" },
  "canopy-2": { url: "https://pbs.twimg.com/profile_images/1924482672530763776/nXHpr2Fp_400x400.png" },
  iota: { url: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTVKpEDLYqke6kdnfUu1h820MBebn0aPjI9Rg&s" },
  hyperliquid: { url: "https://images.cryptorank.io/coins/hyperliquid1699003432264.png" },
  "hyperliquid-evm": { url: "https://images.cryptorank.io/coins/hyperliquid1699003432264.png" },
  // Hemi Bitcoin-aware L2 (chainID 43111 mainnet / 743111 Sepolia) — official
  // brand asset (orange token icon) from docs.hemi.xyz/additional-resources/
  // brand-kit, copied into public/chains/hemi.svg to avoid 3rd-party CDN.
  hemi: { url: "/chains/hemi.svg" },
  "hemi-testnet": { url: "/chains/hemi.svg" },
  // Robinhood Chain (chainId 4663) — brand-provided logo, self-hosted since
  // no Trust Wallet / DefiLlama CDN entry exists for a new custom chain.
  "robinhood-mainnet": { url: "/chains/robinhood-mainnet.png" },
};

/**
 * Ordered rules: first match wins. Prefer TW when known-good; Llama for newer chains.
 */
const NAME_RULES = [
  { re: /\bethereum\b/i, tw: "ethereum" },
  { re: /\bpolygon\b/i, tw: "polygon" },
  { re: /\bavalanche\b|\bavax\b/i, tw: "avalanchec" },
  { re: /\bbinance\s*smart|\bbsc\b|\bnb\s*chain/i, tw: "smartchain" },
  { re: /\barbitrum\b/i, tw: "arbitrum" },
  { re: /\boptimism\b/i, tw: "optimism" },
  { re: /\bbase\b/i, tw: "base" },
  { re: /\blinea\b/i, tw: "linea" },
  { re: /\bmantle\b/i, tw: "mantle" },
  { re: /\bmetis\b/i, tw: "metis" },
  { re: /\blava\b/i, tw: "lava" },
  { re: /\bblast\b/i, tw: "blast" },
  { re: /\bscroll\b/i, tw: "scroll" },
  { re: /\bzksync\b/i, tw: "zksync" },
  { re: /\bzk\s*evm\b|polygon\s*zkevm/i, tw: "polygonzkevm" },
  { re: /\bsei\b/i, tw: "sei" },
  { re: /\bosmosis\b/i, tw: "osmosis" },
  { re: /\baxelar\b/i, tw: "axelar" },
  { re: /\bfilecoin\b/i, tw: "filecoin" },
  { re: /\bnoble\b/i, tw: "noble" },
  { re: /\bakash\b/i, tw: "akash" },
  { re: /\bcosmos\b/i, tw: "cosmos" },
  { re: /\bfantom\b|\bftm\b/i, tw: "fantom" },
  { re: /\bcelo\b/i, tw: "celo" },
  { re: /\bcronos\b/i, tw: "cronos" },
  { re: /\binjective\b/i, tw: "nativeinjective" },
  { re: /\bkava\b/i, tw: "kava" },
  { re: /\bsolana\b/i, tw: "solana" },
  { re: /\bnear\b/i, tw: "near" },
  { re: /\baptos\b/i, tw: "aptos" },
  { re: /\bsui\b/i, tw: "sui" },
  { re: /\bgnosis\b|\bxdai\b/i, tw: "xdai" },
  { re: /\bmoonbeam\b/i, tw: "moonbeam" },
  { re: /\bunichain\b/i, llama: "unichain" },
  { re: /\bstory\b/i, llama: "story" },
  { re: /\boasys\b/i, llama: "oasys" },
  { re: /\bfrax\b/i, llama: "fraxtal" },
  { re: /\bdydx\b/i, llama: "dydx" },
  { re: /\bshannon\b|\bpocket\b/i, url: "https://pbs.twimg.com/profile_images/1882143278604185600/qfiYA7DL_400x400.jpg" },
  { re: /\bcanopy\b/i, url: "https://pbs.twimg.com/profile_images/1924482672530763776/nXHpr2Fp_400x400.png" },
  { re: /\bhemi\b/i, url: "/chains/hemi.svg" },
  { re: /\biota\b/i, url: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTVKpEDLYqke6kdnfUu1h820MBebn0aPjI9Rg&s" },
  { re: /\bhyperliquid\b/i, url: "https://images.cryptorank.io/coins/hyperliquid1699003432264.png" },
];

function firstLogoFromSource(src) {
  if (!src) return null;
  if (src.url && isHttpUrl(src.url)) return src.url.trim();
  if (src.tw) {
    const u = trustWalletLogo(src.tw);
    if (u) return u;
  }
  if (src.llama) {
    const u = llamaChainLogo(src.llama);
    if (u) return u;
  }
  return null;
}

function logoFromSlug(chain) {
  const raw = chain.slug ?? chain.chain_slug ?? chain.short_name;
  const slug = normalizeChainSlug(raw);
  if (!slug) return null;
  const mapped = SLUG_TO_SOURCE[slug];
  if (mapped) {
    const u = firstLogoFromSource(mapped);
    if (u) return u;
  }
  if (/^[a-z0-9]+$/.test(slug)) {
    const directTw = trustWalletLogo(slug);
    if (directTw) return directTw;
  }
  return null;
}

function logoFromChainId(chain) {
  const id = pickNumericChainId(chain);
  if (id == null) return null;
  const twId = CHAIN_ID_TO_TRUSTWALLET[id];
  if (twId) return trustWalletLogo(twId);
  return null;
}

function logoFromName(chain) {
  const name = chain.name;
  if (typeof name !== "string" || !name.trim()) return null;
  for (const rule of NAME_RULES) {
    if (!rule.re.test(name)) continue;
    const u = firstLogoFromSource({ tw: rule.tw, llama: rule.llama, url: rule.url });
    if (u) return u;
  }
  return null;
}

export function resolveChainLogoCandidates(chain) {
  if (!chain || typeof chain !== "object") return [];

  const candidates = [];
  const pushUnique = (url) => {
    if (typeof url !== "string" || !url) return;
    if (!candidates.includes(url)) candidates.push(url);
  };

  const api = logoFromApi(chain);
  if (api) pushUnique(api);

  const id = pickNumericChainId(chain);
  if (id != null) {
    const twId = CHAIN_ID_TO_TRUSTWALLET[id];
    if (twId) {
      pushUnique(trustWalletLogo(twId));
      pushUnique(llamaChainLogo(twId));
    }
  }

  const rawSlug = chain.slug ?? chain.chain_slug ?? chain.short_name;
  const slug = normalizeChainSlug(rawSlug);
  if (slug) {
    if (typeof rawSlug === "string" && rawSlug.trim() && rawSlug.trim().toLowerCase() !== slug) {
      pushUnique(llamaChainLogo(rawSlug.trim().toLowerCase()));
      pushUnique(trustWalletLogo(rawSlug.trim().toLowerCase()));
    }
    const mapped = SLUG_TO_SOURCE[slug];
    if (mapped) {
      pushUnique(firstLogoFromSource(mapped));
      if (mapped.tw) pushUnique(llamaChainLogo(mapped.tw));
      if (mapped.llama) pushUnique(trustWalletLogo(mapped.llama));
    }
    pushUnique(llamaChainLogo(slug));
    pushUnique(trustWalletLogo(slug));
    if (slug === "lava") {
      pushUnique(lavaNetworkLogo());
      pushUnique(llamaChainLogo("lava-mainnet"));
      pushUnique(trustWalletLogo("lava"));
    }
  }

  const byName = logoFromName(chain);
  if (byName) pushUnique(byName);

  if (typeof chain.name === "string" && /\blava\b/i.test(chain.name)) {
    pushUnique(lavaNetworkLogo());
  }

  return candidates;
}

/**
 * @param {Record<string, unknown>} chain — `chain_details` or `chain` from API
 * @returns {string|null}
 */
export function resolveChainLogoUrl(chain) {
  return resolveChainLogoCandidates(chain)[0] || null;
}
